import { useState } from 'react';
import { Card } from '../../../../components/ui/card';
import { cn } from '../../../../lib/utils';
import { normalizeId } from '../../../../utils/notmalizeStr';
import { Skeleton } from '../../../../components/ui/skeleton';
import { truncate } from '../../../../utils/truncate';
import { mindboxViewCategory } from '../../../../lib/mindboxEvents';
import { ProductImage } from '../../../../components/ProductImage';

type CategoryProps = {
  img: string;
  txt: string;
  /** Нужен только аналитике: скролл работает по нормализованному названию. */
  categoryId?: number | string;
  /** 0 = full size, 1 = fully shrunk. Driven directly off scroll position. */
  progress?: number;
  scrollTo: (name: string) => void;
  activeCat: string;
};

const FULL_SIZE = 96;
const SHRUNK_HEIGHT = 32;
const SHRUNK_WIDTH = 120;
const FULL_IMAGE_SIZE = 72;

const lerp = (from: number, to: number, progress: number) =>
  from + (to - from) * progress;

export const CategoryCard = ({
  img,
  txt,
  categoryId,
  progress = 0,
  scrollTo,
  activeCat,
}: CategoryProps) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const isActive = activeCat === normalizeId(txt);
  const shrunk = progress >= 1;

  const height = lerp(FULL_SIZE, SHRUNK_HEIGHT, progress);
  const width = lerp(FULL_SIZE, SHRUNK_WIDTH, progress);
  const imageSize = lerp(FULL_IMAGE_SIZE, 0, progress);

  return (
    <Card
      onClick={() => {
        // Просмотр категории считаем по нажатию: секции проезжают мимо при
        // любом скролле, и наблюдатель за пересечениями шумел бы событиями.
        if (categoryId !== undefined) {
          mindboxViewCategory({ id: categoryId });
        }
        scrollTo(normalizeId(txt));
      }}
      data-category={normalizeId(txt)}
      className={cn(
        'relative shrink-0 flex flex-col border-none shadow-none items-center justify-center rounded-2xl overflow-hidden px-2',
        !shrunk && 'pb-1',
        shrunk && isActive ? 'bg-brand-green' : 'bg-fill-base'
      )}
      style={{ height, width, minHeight: height, minWidth: width }}
    >
      <div
        className="relative flex items-center justify-center overflow-hidden"
        style={{ width: imageSize, height: imageSize, opacity: 1 - progress }}
      >
        {!isImageLoaded && (
          <Skeleton className="w-[72px] h-[72px] rounded-xl bg-white absolute" />
        )}
        <ProductImage
          src={img}
          alt="category"
          loading="lazy"
          onLoad={() => setIsImageLoaded(true)}
          className={cn(
            'object-fill max-w-[72px] max-h-[68px] p-2 transition-opacity duration-300 ease-in-out',
            isImageLoaded ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
      <p
        className={cn(
          'text-caption-sm-medium transition-colors duration-300 ease-in-out',
          !shrunk && 'mt-1 text-text-primary',
          shrunk && (isActive ? 'text-white' : 'text-text-primary')
        )}
      >
        {truncate(txt, 8)}
      </p>
    </Card>
  );
};
